import { Classes, Switch } from "@blueprintjs/core";
import { ButtonContainer, IDataTableColumn, ToolTipButton } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";

import {
  CentreNotificationsGridDto,
  DashboardApi,
  DashboardCentreNotificationsSearch,
  EOrigineSuivi
} from "../../../api";
import { ERoutes } from "../../../AppRouter";
import { LinkButton, SearchTablePage, ViewButton } from "../../../components";
import { useDialog, useEventsContext } from "../../../contexts";
import { useAbortableApiServiceFactory, useApiService, useTabMessage, useTheme, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";
import { Guid } from "../../../utils/guid";

const StyledSwitch = styled(Switch)`
  & .${Classes.SWITCH} {
    &:hover {
      input:checked ~ .${Classes.CONTROL_INDICATOR} {
        background: ${props => props.color};
      }
    }
  }
`;

export interface IDashboardCentreNotificationsProps {}

export const DashboardCentreNotifications: React.FunctionComponent<IDashboardCentreNotificationsProps> = props => {
  const { t } = useTl();
  const { theme } = useTheme();
  const api = useApiService(DashboardApi);
  const apiFactory = useAbortableApiServiceFactory(DashboardApi);
  const lastAbortController = React.useRef<AbortController>();

  const [displayHidden, setDisplayHidden] = React.useState(false);
  const searchFunction = React.useCallback(
    (sObj?: DashboardCentreNotificationsSearch) => {
      sObj.hideMasquees = !displayHidden;
      const { api: abortableApi, abortController } = apiFactory();
      lastAbortController.current = abortController;
      return abortableApi.dashboardSearchCentreNotifications({ DashboardCentreNotificationsSearch: sObj });
    },
    [apiFactory, displayHidden]
  );

  const identifier = React.useMemo(() => Guid.newGuid(), []);
  const { openTabThenSendMessage } = useTabMessage(identifier);

  const onPersonneClick = React.useCallback((row: CentreNotificationsGridDto) => {
    let url = "";

    if (row.idapprenant) url = `#${ERoutes.apprenant}/${row.idapprenant}/signaletique/edit`;
    if (row.idrepresentant) url = `#${ERoutes.representant}/${row.idrepresentant}/detail/edit`;
    if (row.idformateur) url = `#${ERoutes.formateur}/${row.idformateur}/signaletique/edit`;
    if (row.idtuteur) url = `#${ERoutes.tuteur}/${row.idtuteur}/detail/edit`;
    if (row.idcontact) url = `#${ERoutes.contacts}/${row.idcontact}/detail/edit`;
    if (row.idchefEntreprise) url = `#${ERoutes.chefEntreprise}/${row.idchefEntreprise}/detail/edit`;

    window.open(url, "_blank");
  }, []);

  const [rowLoading, setRowLoading] = React.useState(null);
  const { dispatchEvent } = useEventsContext();
  const { showDialogPromise } = useDialog();
  const masquerSuivi = React.useCallback(
    async (idsuivi: number) => {
      const res = await showDialogPromise({
        title: t(ETLCodes.MasquerSuiviConfirmTitle),
        message: t(ETLCodes.MasquerSuiviConfirmMessage)
      });
      if (res === "yes") {
        setRowLoading(idsuivi);
        await api.dashboardMasquerSuivi({
          idsuivi
        });
        setRowLoading(null);
        dispatchEvent("SEARCH_TABLE_REFRESH");
        dispatchEvent("DASHBOARD_COUNTS_REFRESH_Suivis");
      }
    },
    [api, dispatchEvent, showDialogPromise, t]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: CentreNotificationsGridDto) => (
          <ButtonContainer>
            {!row.masquee && (
              <ToolTipButton
                tooltipPosition="bottom"
                tooltip={t(ETLCodes.MasquerSuivi)}
                minimal
                icon="cross"
                onClick={() => masquerSuivi(row.idsuivi)}
                loading={!!rowLoading && rowLoading === row.idsuivi}
                disabled={!!rowLoading && rowLoading !== row.idsuivi}
              />
            )}
            <ViewButton
              minimal={true}
              onClick={() =>
                openTabThenSendMessage(`#${ERoutes.suivi}/${row.idsuivi}/detail/view/hope`, "FromDashboard")
              }
            />
          </ButtonContainer>
        )
      },
      {
        header: () => t(ETLCodes.Beneficiaire),
        fieldName: "beneficiaire",
        render: (row: CentreNotificationsGridDto) => (
          <LinkButton text={row.beneficiaire} onClick={() => onPersonneClick(row)} />
        )
      },
      {
        header: () => t(ETLCodes.DateSuivi),
        fieldName: "dateSuivi"
      },
      {
        header: () => t(ETLCodes.Source),
        fieldName: "source",
        render: (row: CentreNotificationsGridDto) =>
          row.origineSuivi === EOrigineSuivi.Services ? row.service : t(ETLCodes.Smile)
      },
      {
        header: () => t(ETLCodes.RealisateurSuivi),
        fieldName: "realisateurSuivi",
        render: (row: CentreNotificationsGridDto) => (
          <LinkButton
            text={row.realisateurSuivi}
            onClick={() => window.open(`#${ERoutes.users}/${row.idrealisateurSuivi}/detail/view`, "_blank")}
          />
        )
      },
      {
        header: () => t(ETLCodes.Encodeur),
        fieldName: "encodeur",
        render: (row: CentreNotificationsGridDto) => (
          <LinkButton
            text={row.encodeur}
            onClick={() => window.open(`#${ERoutes.users}/${row.idencodeur}/detail/view`, "_blank")}
          />
        )
      },
      {
        header: () => t(ETLCodes.TypeSuivi),
        fieldName: "typeSuivi"
      },
      {
        header: () => t(ETLCodes.SousTypeSuivi),
        fieldName: "sousTypeSuivi"
      }
    ],
    [masquerSuivi, onPersonneClick, openTabThenSendMessage, rowLoading, t]
  );

  const getCriteria = React.useCallback(async () => {
    return await api.dashboardGetCentreNotificationsCriterias();
  }, [api]);

  const onAbort = React.useCallback(() => lastAbortController.current?.abort(), []);

  return (
    <>
      <SearchTablePage
        searchStateInitialSearch={false}
        withCard={false}
        columns={columns}
        getCriteriasFunction={getCriteria}
        searchFunction={searchFunction}
        onAbort={onAbort}
        sortKeys={{ dateSuivi: "DESC" }}
        rightElement={
          <StyledSwitch
            color={theme.primaryColor}
            label={t(ETLCodes.AfficherMasquees)}
            checked={displayHidden}
            onChange={e => {
              setDisplayHidden(e.currentTarget.checked);
            }}
          ></StyledSwitch>
        }
      />
    </>
  );
};
